import React, {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { ScaleLoader } from "react-spinners";
import classNames from "classnames";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { searchMedicineItems } from "../../services/medicineSearchService";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import { ConsultPharmacySelection } from "../../types/consultOrder.types";
import { useConsultOrderContext } from "../../contexts/ConsultOrderContext";
import ItemStockOverviewModal from "./ItemStockOverviewModal";
import styles from "./OrderItemPicker.module.scss";

// ITAM-2583/ITAM-2613: item type-ahead for the consult order. Searches the
// GLOBAL catalog (POST /search/item), stock-agnostic, so the advisor finds an
// item even when the picked pharmacy has no stock. Selecting a result opens
// the stacked ItemStockOverviewModal (per-pharmacy stock + the only add path);
// the item reaches the shared order DRAFT only through that modal's add button.
//
// ITAM-2887: the pharmacy is the draft's own pick (a prop), not a fixed pair.

// Square thumbnail size (px). Fixed width/height reserves the box before the
// image loads, so rows never reflow (no layout shift).
const IMAGE_SIZE = 40;

// /search/item returns relative file paths, so the URL is built from the
// first IMAGE file's `location`, prefixed with the CDN base.
const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";

const firstImageUrl = (item: ItemBasicResponse): string | undefined => {
  const image = item?.files?.find((file) => file.type === "IMAGE");
  return image ? `${CDN_BASE}${image.location}` : undefined;
};

/**
 * Decorative product thumbnail. On load failure it is replaced by a same-size
 * placeholder so the row keeps its fixed height (no layout shift).
 */
const OptionImage = ({ src }: { src: string }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span
        className={styles.optionImageFallback}
        data-testid="order-option-image-fallback"
        aria-hidden="true"
      />
    );
  }

  return (
    <img
      className={styles.optionImage}
      src={src}
      alt=""
      width={IMAGE_SIZE}
      height={IMAGE_SIZE}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

export interface OrderItemPickerProps {
  /** The pharmacy the items are added for; null disables the picker. */
  pharmacy: ConsultPharmacySelection | null;
}

const OrderItemPicker: React.FC<OrderItemPickerProps> = ({ pharmacy }) => {
  const { addItem } = useConsultOrderContext();

  const { query, onQueryChange, results, status, loading, reset } =
    useDebouncedSearch<ItemBasicResponse>(searchMedicineItems);

  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  // The dropdown selection whose stock overview is being shown (modal open).
  const [selectedItem, setSelectedItem] = useState<ItemBasicResponse | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);

  const reactId = useId();
  const inputId = `order-item-picker-${reactId}-input`;
  const listboxId = `order-item-picker-${reactId}-listbox`;
  const optionId = (item: ItemBasicResponse) =>
    `${listboxId}-opt-${item.id}`;

  // Reset the active-descendant cursor whenever the result set changes, so a
  // stale cursor can never index into a different (or shorter) list.
  useEffect(() => {
    setCursor(-1);
  }, [results]);

  // Containment-aware click-away: while open (including DURING loading) a
  // mousedown outside the root closes the dropdown. Option selection happens on
  // the option's own onMouseDown (inside the root), so it is never an outside
  // click.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [open]);

  // Disjoint flags: a status row is NEVER part of the keyboard-nav list.
  const showResultsList = open && status === "results" && results.length > 0;
  const showStatusRow = open && (status === "empty" || status === "error");
  const showDropdown = showResultsList || showStatusRow;

  // Selecting opens the stock-overview modal; it does NOT add and does NOT
  // reset the query, so closing the modal without adding returns the advisor
  // to their search.
  const selectItem = useCallback((item: ItemBasicResponse) => {
    setSelectedItem(item);
    setOpen(false);
    setCursor(-1);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQueryChange(e.target.value);
    setOpen(true);
  };

  // Keyboard handling is SCOPED to the input; it never listens on `window`, so
  // Enter in another field can never fire a selection here.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      // ALWAYS swallow Enter so it can never implicitly submit a containing
      // <form>. Select only when a real, in-range result is highlighted.
      e.preventDefault();
      if (showResultsList && cursor >= 0 && cursor < results.length) {
        selectItem(results[cursor]);
      }
      return;
    }
    if (!showResultsList) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c < results.length - 1 ? c + 1 : c));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c > 0 ? c - 1 : 0));
    }
  };

  const activeDescendant =
    showResultsList && cursor >= 0 && cursor < results.length
      ? optionId(results[cursor])
      : undefined;

  // The search itself is global, but ADDING is impossible without a pharmacy
  // (the stock modal pins and maps by its composite id), so the picker stays
  // gated on the pick.
  if (!pharmacy) {
    return (
      <div className={styles.OrderItemPicker}>
        <label className={styles.label} htmlFor={inputId}>
          Pretraga artikala
        </label>
        <input
          id={inputId}
          className={styles.input}
          data-consult-field="items"
          type="text"
          disabled
          placeholder="Pretražite artikle"
        />
        <p className={styles.hint} role="note">
          Izaberite apoteku da biste pretražili artikle.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.OrderItemPicker} ref={rootRef}>
      <label className={styles.label} htmlFor={inputId}>
        Pretraga artikala
      </label>
      <div className={styles.inputWrapper}>
        <input
          id={inputId}
          className={styles.input}
          data-consult-field="items"
          type="text"
          name="order-item-search-box"
          autoComplete="off"
          maxLength={64}
          placeholder="Pretražite artikle"
          role="combobox"
          aria-expanded={showResultsList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
        />
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <ScaleLoader color="#00b2b1" height={14} width={3} />
          </span>
        )}

        {showDropdown && (
          <>
            {showStatusRow && (
              <div className={styles.statusRow} role="status">
                {status === "empty"
                  ? `Nema artikala koji odgovaraju pretrazi "${query.trim()}".`
                  : "Greška, pokušajte ponovo"}
              </div>
            )}
            {showResultsList && (
              <ul className={styles.listbox} id={listboxId} role="listbox">
                {results.map((item, i) => {
                  const imageUrl = firstImageUrl(item);
                  return (
                    <li
                      key={item.id}
                      id={optionId(item)}
                      className={classNames(styles.option, {
                        [styles.optionActive]: i === cursor,
                      })}
                      role="option"
                      aria-selected={i === cursor}
                      onMouseEnter={() => setCursor(i)}
                      onMouseDown={(e) => {
                        // Commit on pointer-down (preventDefault keeps focus) so
                        // selection lands before any focus change; never onBlur.
                        e.preventDefault();
                        selectItem(item);
                      }}
                    >
                      {imageUrl && <OptionImage src={imageUrl} />}
                      <span className={styles.optionMain}>
                        <span className={styles.optionName}>{item.name}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>

      {selectedItem && (
        <ItemStockOverviewModal
          key={`${pharmacy.marketplaceId}:${pharmacy.branchMarketplaceId}`}
          item={selectedItem}
          marketplaceId={pharmacy.marketplaceId}
          branchMarketplaceId={pharmacy.branchMarketplaceId}
          marketplaceName={pharmacy.branchName ?? pharmacy.name}
          onAdd={(draftItem) => {
            addItem(draftItem);
            setSelectedItem(null);
            reset(); // successful add clears the search, as before
          }}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default OrderItemPicker;
